import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  FileDownload as FileDownloadIcon,
  PlayArrow as RunIcon,
  PlaylistAdd as PlaylistAddIcon,
  Stop as StopIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
} from "material-react-table";
import React, { useMemo, useState } from "react";
import { useEntityExport } from "../../../hooks/useEntityExport";
import {
  ResearchEntity,
  WorkflowStatus,
} from "../../../types/researchWorkflow";
import { getLabelDisplay } from "../../../utils/labelMapping";
import AddCandidatesModal from "../shared/AddCandidatesModal";
import { selectVisibleDeselectAllProps } from "../shared/selectionProps";
import DiscoveryRunModal from "./DiscoveryRunModal";
import ExportFilterModal, { ExportFilter } from "./ExportFilterModal";
import RejectionReasonModal from "./RejectionReasonModal";

interface LongListViewProps {
  entities: ResearchEntity[];
  trackId: string;
  onApprove: (entityId: string) => void;
  onReject: (entityId: string, reason: string) => void;
  onBulkApprove: (entityIds: string[]) => void;
  onBulkReject: (entityIds: string[]) => void;
  onBulkFinal: (entityIds: string[]) => void;
  onRunDiscovery: (targetCandidates: number) => void;
  onStopDiscovery: () => void;
  onEntityClick?: (entityId: string) => void;
  onCandidatesAdded?: () => void;
  isRunning?: boolean;
  isLoading?: boolean;
  isViewer?: boolean;
}

const LongListView: React.FC<LongListViewProps> = ({
  entities,
  trackId,
  onApprove,
  onReject,
  onBulkApprove,
  onBulkReject,
  onBulkFinal,
  onRunDiscovery,
  onStopDiscovery,
  onEntityClick,
  onCandidatesAdded,
  isRunning = false,
  isLoading = false,
  isViewer = false,
}) => {
  const [statusFilter, setStatusFilter] = useState<
    "all" | "candidate" | "approved" | "rejected" | "lowscore" | "highscore"
  >("candidate");
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedEntityForReject, setSelectedEntityForReject] =
    useState<ResearchEntity | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [discoveryModalOpen, setDiscoveryModalOpen] = useState(false);
  const [addCandidatesOpen, setAddCandidatesOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const {
    exportToExcel,
    isExporting,
    error: exportError,
    clearError,
  } = useEntityExport(trackId, "longlist");

  // Persist table state across data updates
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const [sorting, setSorting] = useState([
    { id: "relevanceScore", desc: true },
  ]);

  // Filter entities based on workflow status
  const filteredEntities = useMemo(() => {
    const longListStatuses: WorkflowStatus[] = [
      "candidate",
      "approved",
      "rejected",
      "final",
    ];
    const longListEntities = entities.filter((e) =>
      longListStatuses.includes(e.workflowStatus),
    );

    let filtered: ResearchEntity[];
    if (statusFilter === "all") {
      filtered = longListEntities;
    } else if (statusFilter === "approved") {
      // Show 'final' entities in the 'approved' filter
      filtered = longListEntities.filter(
        (e) => e.workflowStatus === "approved" || e.workflowStatus === "final",
      );
    } else if (statusFilter === "candidate") {
      // Show only candidates with high or medium potential, plus manual adds
      filtered = longListEntities.filter(
        (e) =>
          e.workflowStatus === "candidate" &&
          (e.matchCategory === "high_potential" ||
            e.matchCategory === "medium_potential" ||
            e.matchCategory === "manual"),
      );
    } else if (statusFilter === "highscore") {
      // Show all entities with high or medium potential, plus manual adds (any status)
      filtered = longListEntities.filter(
        (e) =>
          e.matchCategory === "high_potential" ||
          e.matchCategory === "medium_potential" ||
          e.matchCategory === "manual",
      );
    } else if (statusFilter === "lowscore") {
      // Show candidates with low potential
      filtered = longListEntities.filter(
        (e) =>
          e.workflowStatus === "candidate" &&
          e.matchCategory === "low_potential",
      );
    } else {
      filtered = longListEntities.filter(
        (e) => e.workflowStatus === statusFilter,
      );
    }

    return filtered;
  }, [entities, statusFilter]);

  const handleRejectClick = (entity: ResearchEntity) => {
    setSelectedEntityForReject(entity);
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = (reason: string) => {
    if (selectedEntityForReject) {
      onReject(selectedEntityForReject.id, reason);
    }
    setRejectModalOpen(false);
    setSelectedEntityForReject(null);
  };

  const handleExport = (filter: ExportFilter) => {
    if (filter === "selection") {
      const selectedIds = Object.keys(rowSelection).filter(
        (key) => rowSelection[key],
      );
      exportToExcel(undefined, selectedIds);
      setExportModalOpen(false);
      return;
    }

    let decisionStates: string[] | undefined;

    switch (filter) {
      case "all":
        decisionStates = undefined; // Export all
        break;
      case "candidates":
        decisionStates = ["candidate"]; // Backend filters score >= 50
        break;
      case "rejected":
        decisionStates = ["rejected"];
        break;
      case "approved":
        decisionStates = ["approved"];
        break;
      case "final":
        decisionStates = ["final"];
        break;
      case "high_score":
        // Export all entities with score >= 50
        decisionStates = ["high_score"];
        break;
      case "low_score":
        // Export all entities with score < 50
        decisionStates = ["low_score"];
        break;
    }

    exportToExcel(decisionStates);
    setExportModalOpen(false);
  };

  const columns = useMemo<MRT_ColumnDef<ResearchEntity>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 250,
        Cell: ({ cell, row }) => (
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{
              cursor: "pointer",
              "&:hover": {
                color: "primary.light",
                textDecoration: "underline",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onEntityClick) {
                onEntityClick(row.original.kg_entity_id);
              }
            }}
          >
            {String(cell.getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: "home_country",
        header: "Country",
        size: 100,
        Cell: ({ cell, row }) => (
          <Typography variant="body2">
            {String(cell.getValue() || row.original.country_iso2 || "")}
          </Typography>
        ),
      },
      {
        accessorKey: "tags",
        header: "Tags",
        size: 200,
        Cell: ({ cell }) => {
          const tags = cell.getValue() as string[] | undefined;
          if (!tags || tags.length === 0) return null;
          return (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {tags.slice(0, 2).map((tag, idx) => {
                const labelInfo = getLabelDisplay(tag);
                return (
                  <Tooltip key={idx} title={labelInfo.full} arrow>
                    <Chip
                      label={labelInfo.abbrev}
                      size="small"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  </Tooltip>
                );
              })}
              {tags.length > 2 && (
                <Tooltip
                  title={tags
                    .slice(2)
                    .map((t) => getLabelDisplay(t).full)
                    .join(", ")}
                  arrow
                >
                  <Chip
                    label={`+${tags.length - 2}`}
                    size="small"
                    sx={{ fontSize: "0.7rem" }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "relevanceScore",
        header: "Relevance",
        size: 100,
        Cell: ({ row }) => {
          const reasoning = row.original.evaluationReasoning;

          const category = row.original.matchCategory;
          const categoryDisplay: Record<
            string,
            {
              label: string;
              color: "success" | "warning" | "default" | "error" | "info";
            }
          > = {
            high_potential: { label: "High", color: "success" },
            medium_potential: { label: "Medium", color: "warning" },
            low_potential: { label: "Low", color: "default" },
            irrelevant: { label: "Irrelevant", color: "error" },
            manual: { label: "Manual", color: "info" },
          };
          const display = category ? categoryDisplay[category] : undefined;
          const scoreChip = (
            <Chip
              label={display?.label ?? "?"}
              size="small"
              color={display?.color ?? "default"}
              sx={{ fontWeight: 600 }}
            />
          );

          // Build tooltip from discovery_meta (always available for discovery-stage entities)
          const discoveryMeta = row.original.latest_reference?.discovery_meta;
          let tooltipContent = null;
          const hasSignals =
            discoveryMeta?.match_signals &&
            discoveryMeta.match_signals.length > 0;
          const isInactive = !!row.original.inactive;
          const hasRejection = !!discoveryMeta?.rejection_reason;
          const hasManualReason = category === "manual" && !!reasoning;
          const hasManualAddedBy =
            category === "manual" && !!discoveryMeta?.added_by;
          const hasVersion =
            row.original.specVersionNumber !== undefined &&
            row.original.specVersionNumber !== null;

          if (
            hasSignals ||
            isInactive ||
            hasRejection ||
            hasManualReason ||
            hasManualAddedBy ||
            hasVersion
          ) {
            tooltipContent = (
              <Box sx={{ maxWidth: 400, p: 1.5 }}>
                {isInactive && (
                  <Chip
                    label="Inactive organization"
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ mb: 1, fontWeight: 600 }}
                  />
                )}
                {hasSignals && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, display: "block" }}
                    >
                      Match Signals:
                    </Typography>
                    {discoveryMeta?.match_signals?.map((signal, idx) => (
                      <Typography
                        key={idx}
                        variant="caption"
                        sx={{ display: "block" }}
                      >
                        • {signal}
                      </Typography>
                    ))}
                  </Box>
                )}
                {hasRejection && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        display: "block",
                        color: "error.main",
                      }}
                    >
                      Dismissal Reason:
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block" }}>
                      {discoveryMeta?.rejection_reason}
                    </Typography>
                  </Box>
                )}
                {hasManualReason && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, display: "block" }}
                    >
                      Reason:
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block" }}>
                      {reasoning}
                    </Typography>
                  </Box>
                )}
                {hasManualAddedBy && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "text.secondary",
                      mt: 0.5,
                    }}
                  >
                    Added by: {discoveryMeta?.added_by}
                  </Typography>
                )}
                {hasVersion && (
                  <Box
                    sx={{
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontSize: "0.7rem" }}
                    >
                      Based on Requirements Version{" "}
                      {row.original.specVersionNumber}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          }

          return tooltipContent ? (
            <Tooltip
              title={tooltipContent}
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "background.paper",
                    color: "text.primary",
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: 3,
                    "& .MuiTooltip-arrow": {
                      color: "background.paper",
                      "&::before": {
                        border: "1px solid",
                        borderColor: "divider",
                      },
                    },
                  },
                },
              }}
            >
              {scoreChip}
            </Tooltip>
          ) : (
            scoreChip
          );
        },
      },
      {
        accessorKey: "workflowStatus",
        header: "Status",
        size: 120,
        Cell: ({ cell }) => {
          const status = cell.getValue() as WorkflowStatus;
          return (
            <Chip
              label={
                status === "approved"
                  ? "Evaluation"
                  : status === "rejected"
                    ? "Dismissed"
                    : status
              }
              size="small"
              color={
                status === "approved"
                  ? "success"
                  : status === "rejected"
                    ? "error"
                    : "default"
              }
              sx={{ textTransform: "capitalize" }}
            />
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 150,
        Cell: ({ row }) => {
          const entity = row.original;
          const isFinal = entity.workflowStatus === "final";
          return (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {entity.workflowStatus === "candidate" && (
                <>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => onApprove(entity.id)}
                  >
                    <ApproveIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRejectClick(entity)}
                  >
                    <RejectIcon fontSize="small" />
                  </IconButton>
                </>
              )}
              {entity.workflowStatus === "approved" && (
                <IconButton
                  size="small"
                  onClick={() => onReject(entity.id, "Reverted to candidate")}
                >
                  <RejectIcon fontSize="small" />
                </IconButton>
              )}
              {entity.workflowStatus === "rejected" && (
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => onApprove(entity.id)}
                >
                  <ApproveIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                color={isFinal ? "warning" : "default"}
                onClick={() => !isFinal && onBulkFinal([entity.id])}
                sx={isFinal ? { cursor: "default" } : undefined}
              >
                {isFinal ? (
                  <StarIcon fontSize="small" />
                ) : (
                  <StarBorderIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [onApprove, onReject, onBulkFinal],
  );

  const visibleColumns = isViewer
    ? columns.filter((col) => col.id !== "actions")
    : columns;

  const candidateCount = entities.filter(
    (e) =>
      e.workflowStatus === "candidate" &&
      (e.matchCategory === "high_potential" ||
        e.matchCategory === "medium_potential" ||
        e.matchCategory === "manual"),
  ).length;
  const lowScoreCount = entities.filter(
    (e) =>
      e.workflowStatus === "candidate" && e.matchCategory === "low_potential",
  ).length;
  const approvedCount = entities.filter(
    (e) => e.workflowStatus === "approved" || e.workflowStatus === "final",
  ).length;
  const rejectedCount = entities.filter(
    (e) => e.workflowStatus === "rejected",
  ).length;
  const highScoreCount = entities.filter(
    (e) =>
      ["candidate", "approved", "rejected", "final"].includes(
        e.workflowStatus,
      ) &&
      (e.matchCategory === "high_potential" ||
        e.matchCategory === "medium_potential" ||
        e.matchCategory === "manual"),
  ).length;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header Controls */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight="600" gutterBottom>
            Discovery Candidates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and advance candidates for evaluation
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          {!isViewer && (
            <Button
              variant="outlined"
              startIcon={<PlaylistAddIcon />}
              onClick={() => setAddCandidatesOpen(true)}
              size="large"
            >
              Add Candidates
            </Button>
          )}
          {!isViewer && (
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={() => setExportModalOpen(true)}
              disabled={isExporting || filteredEntities.length === 0}
              size="large"
            >
              {isExporting ? "Exporting..." : "Export to Excel"}
            </Button>
          )}
          {!isViewer && (isRunning ? (
            <Button
              variant="contained"
              color="error"
              startIcon={<StopIcon />}
              onClick={onStopDiscovery}
              size="large"
            >
              Stop
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<RunIcon />}
              onClick={() => setDiscoveryModalOpen(true)}
              size="large"
            >
              Run Discovery
            </Button>
          ))}
        </Box>
      </Box>

      {/* Status Filter + Bulk Actions */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(_, newValue) => newValue && setStatusFilter(newValue)}
          size="small"
          sx={{ backgroundColor: "background.paper" }}
        >
          <ToggleButton value="candidate">
            Candidates ({candidateCount})
          </ToggleButton>
          <ToggleButton value="approved">
            Evaluation ({approvedCount})
          </ToggleButton>
          <ToggleButton value="rejected">
            Dismissed ({rejectedCount})
          </ToggleButton>
          <ToggleButton value="highscore">
            High Relevance ({highScoreCount})
          </ToggleButton>
          <ToggleButton value="lowscore">
            Low Relevance ({lowScoreCount})
          </ToggleButton>
          <ToggleButton value="all">
            All (
            {candidateCount + lowScoreCount + approvedCount + rejectedCount})
          </ToggleButton>
        </ToggleButtonGroup>

        {!isViewer &&
          Object.keys(rowSelection).filter((key) => rowSelection[key]).length >
            0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {
                Object.keys(rowSelection).filter((key) => rowSelection[key])
                  .length
              }{" "}
              selected
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ApproveIcon />}
              onClick={() => {
                const selectedIds = Object.keys(rowSelection).filter(
                  (key) => rowSelection[key],
                );
                onBulkApprove(selectedIds);
                setRowSelection({});
              }}
            >
              Evaluate
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RejectIcon />}
              onClick={() => {
                const selectedIds = Object.keys(rowSelection).filter(
                  (key) => rowSelection[key],
                );
                onBulkReject(selectedIds);
                setRowSelection({});
              }}
            >
              Dismiss
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<StarIcon />}
              onClick={() => {
                const selectedIds = Object.keys(rowSelection).filter(
                  (key) => rowSelection[key],
                );
                onBulkFinal(selectedIds);
                setRowSelection({});
              }}
            >
              Final Selection
            </Button>
          </Box>
        )}
      </Box>

      {/* Entity Table */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <MaterialReactTable
          columns={visibleColumns}
          data={filteredEntities}
          enableColumnResizing
          enableStickyHeader
          enableBottomToolbar
          enableTopToolbar={false}
          enableRowSelection
          getRowId={(row) => row.id}
          onRowSelectionChange={setRowSelection}
          muiSelectCheckboxProps={{ title: "" }}
          // Select adds the visible page; a fully-selected page clears the
          // whole id-keyed selection, including rows parked on other pages.
          muiSelectAllCheckboxProps={selectVisibleDeselectAllProps(
            setRowSelection,
            { title: "" },
          )}
          // Keep the user on their current page when polling refetches data
          // (completes the "persist table state across data updates" intent
          // above); the component remounts (→ page 1) on stage/view switch.
          autoResetPageIndex={false}
          state={{
            isLoading: isLoading,
            showProgressBars: isLoading,
            pagination,
            sorting,
            rowSelection,
          }}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          initialState={{
            density: "compact",
          }}
          muiTablePaperProps={{
            elevation: 0,
            sx: {
              height: "100%",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "background.paper",
            },
          }}
          muiTableContainerProps={{
            sx: { flex: 1 },
          }}
          muiBottomToolbarProps={{
            sx: { backgroundColor: "#1e1e1e" },
          }}
        />
      </Box>

      {/* Rejection Modal */}
      <RejectionReasonModal
        open={rejectModalOpen}
        entityName={selectedEntityForReject?.name || ""}
        onClose={() => {
          setRejectModalOpen(false);
          setSelectedEntityForReject(null);
        }}
        onConfirm={handleRejectConfirm}
      />

      {/* Add Candidates Modal */}
      <AddCandidatesModal
        open={addCandidatesOpen}
        onClose={() => setAddCandidatesOpen(false)}
        trackId={trackId}
        initialDecisionState="candidate"
        onCandidatesAdded={() => onCandidatesAdded?.()}
      />

      {/* Export Filter Modal */}
      <ExportFilterModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onExport={handleExport}
        isExporting={isExporting}
        selectedCount={
          Object.keys(rowSelection).filter((key) => rowSelection[key]).length
        }
      />

      {/* Discovery Run Modal */}
      <DiscoveryRunModal
        open={discoveryModalOpen}
        onClose={() => setDiscoveryModalOpen(false)}
        onConfirm={onRunDiscovery}
      />

      {/* Export Error Snackbar */}
      <Snackbar
        open={!!exportError}
        autoHideDuration={6000}
        onClose={clearError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={clearError} severity="error" sx={{ width: "100%" }}>
          {exportError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LongListView;
